import { request } from "./http";
import { normalizeDiaryEntry } from "./diaryApi";

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function extractItems(raw) {
  if (Array.isArray(raw)) return raw;
  const obj = asObject(raw);
  if (Array.isArray(obj.content)) return obj.content;
  if (Array.isArray(obj.items)) return obj.items;
  if (Array.isArray(obj.items?.content)) return obj.items.content;
  if (Array.isArray(obj.item?.items)) return obj.item.items;
  if (Array.isArray(obj.item?.content)) return obj.item.content;
  if (Array.isArray(obj.item)) return obj.item;
  return [];
}

function withQuery(path, { page = 0, size = 50, q = "", status = [], inactiveDaysGt, sort } = {}) {
  const qs = new URLSearchParams();

  if (page != null) qs.set("page", String(Number(page) || 0));
  if (size != null) qs.set("size", String(Number(size) || 50));

  const preparedQ = String(q || "").trim();
  if (preparedQ) qs.set("q", preparedQ);

  const statuses = Array.isArray(status) ? status : status ? [status] : [];
  statuses
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .forEach((item) => qs.append("status", item.toUpperCase()));

  if (inactiveDaysGt != null && Number.isFinite(Number(inactiveDaysGt))) {
    qs.set("inactiveDaysGt", String(Number(inactiveDaysGt)));
  }

  const preparedSort = String(sort || "").trim();
  if (preparedSort) qs.set("sort", preparedSort);

  const tail = qs.toString();
  return tail ? `${path}?${tail}` : path;
}

function unwrapItem(raw) {
  const obj = asObject(raw);
  if (obj.item && typeof obj.item === "object") return obj.item;
  return obj;
}

function resolveInviteLink(raw) {
  const obj = unwrapItem(raw);
  const maybe = obj?.inviteLink ?? obj?.link ?? obj?.url ?? obj?.inviteUrl ?? "";
  const link = String(maybe || "").trim();
  return link || "";
}

function normalizePsychologistClientNote(item = {}) {
  const raw = asObject(item);
  return {
    id: raw.id ?? raw.noteId ?? null,
    psychologistUserId: raw.psychologistUserId ?? null,
    clientUserId: raw.clientUserId ?? null,
    shortLabel: String(raw.shortLabel || raw.title || "").trim(),
    note: String(raw.note || raw.content || raw.text || "").trim(),
    createdAt: raw.createdAt || raw.createdWhen || null,
    updatedAt: raw.updatedAt || raw.updatedWhen || raw.lastModified || null,
  };
}

function toIntOrNull(value) {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : null;
}

function toPayloadCandidates(clientId, payload = {}) {
  const startDateTime = String(payload?.startDateTime || payload?.startAt || payload?.start || "").trim();
  const endDateTime = String(payload?.endDateTime || payload?.endAt || payload?.end || "").trim();
  const price = toIntOrNull(payload?.price ?? payload?.priceAtTime ?? payload?.amount ?? 0);
  const id = String(clientId || "").trim();

  return [
    {
      startDateTime,
      endDateTime,
      price,
      priceAtTime: price,
      amount: price,
      clientId: id,
      client_id: id,
      skipPayment: true,
      withoutPayment: true,
      freeBooking: true,
      isFree: true,
    },
    {
      startAt: startDateTime,
      endAt: endDateTime,
      price,
      clientId: id,
      skipPayment: true,
      isFree: true,
    },
  ];
}

function canTryFallback(err) {
  const status = Number(err?.status ?? 0);
  const code = String(err?.code || err?.error || "").trim().toUpperCase();
  if (!status) return true;
  if (status === 401) return false;
  if (code === "CLIENT_HAS_NO_LINKED_USER") return false;
  return true;
}

function canFallbackEndpoint(err) {
  const status = Number(err?.status ?? err?.response?.status ?? 0);
  return status === 404 || status === 405 || status === 501;
}

async function requestFirstAvailable(paths = [], options = {}) {
  let lastErr = null;
  for (const path of paths) {
    try {
      return await request(path, options);
    } catch (err) {
      if (!canFallbackEndpoint(err)) throw err;
      lastErr = err;
    }
  }
  if (lastErr) throw lastErr;
  throw new Error("No endpoint paths configured");
}

export const CLIENT_STATUSES = Object.freeze(["ACTIVE", "NEW", "WAITING", "ARCHIVE"]);
export const SESSION_STATUSES = Object.freeze(["PLANNED", "COMPLETED", "CANCELLED", "NO_SHOW"]);

export const psychologistClientsApi = {
  list(params = {}) {
    return request(withQuery("/api/psychologist/clients", params));
  },

  listItems(params = {}) {
    return this.list(params).then((raw) => ({ raw, items: extractItems(raw) }));
  },

  create(payload = {}) {
    return request("/api/psychologist/clients", {
      method: "POST",
      json: payload,
    }).then(unwrapItem);
  },

  update(clientId, payload = {}) {
    return request(`/api/psychologist/clients/${encodeURIComponent(String(clientId))}`, {
      method: "PATCH",
      json: payload,
    }).then(unwrapItem);
  },

  updateStatus(clientId, status) {
    return request(`/api/psychologist/clients/${encodeURIComponent(String(clientId))}/status`, {
      method: "PATCH",
      json: { status },
    }).then(unwrapItem);
  },

  listSessions(clientId, { page = 0, size = 20 } = {}) {
    const path = withQuery(`/api/psychologist/clients/${encodeURIComponent(String(clientId))}/sessions`, {
      page,
      size,
    });
    return request(path).then(extractItems);
  },

  async createBooking(clientId, payload = {}, options = {}) {
    const id = String(clientId || "").trim();
    if (!id) throw new Error("clientId is required");

    const psychologistId = String(options?.psychologistId || "").trim();
    const [clientScopedPayload, psychologistScopedPayload] = toPayloadCandidates(id, payload);
    const paths = [
      {
        path: `/api/psychologist/clients/${encodeURIComponent(id)}/bookings`,
        payload: clientScopedPayload,
      },
      psychologistId
        ? {
            path: `/api/psychologists/${encodeURIComponent(psychologistId)}/bookings`,
            payload: psychologistScopedPayload || clientScopedPayload,
          }
        : null,
    ].filter(Boolean);

    let lastError = null;
    for (const attempt of paths) {
      try {
        return await request(attempt.path, { method: "POST", json: attempt.payload });
      } catch (err) {
        lastError = err;
        if (!canTryFallback(err)) throw err;
      }
    }

    if (lastError?.message) {
      const errorId = String(lastError?.errorId || "").trim();
      if (errorId) {
        throw new Error(`${lastError.message} (errorId: ${errorId})`);
      }
    }
    throw lastError || new Error("Не удалось создать запись клиента");
  },


  listClientDiaryEntries(clientUserId, { q = "", page = 0, size = 50 } = {}) {
    const id = String(clientUserId || "").trim();
    if (!id) return Promise.reject(new Error("clientUserId is required"));
    const qs = new URLSearchParams();
    qs.set("page", String(Math.max(0, Number(page) || 0)));
    qs.set("size", String(Math.max(1, Number(size) || 50)));
    if (String(q || "").trim()) qs.set("q", String(q).trim());
    return request(`/api/psychologist/clients/${encodeURIComponent(id)}/diary/entries?${qs.toString()}`)
      .then((raw) => extractItems(raw?.items ?? raw).map(normalizeDiaryEntry));
  },

  getClientDiaryEntry(clientUserId, entryId) {
    const clientId = String(clientUserId || "").trim();
    const id = String(entryId || "").trim();
    if (!clientId || !id) return Promise.reject(new Error("clientUserId and entryId are required"));
    return request(`/api/psychologist/clients/${encodeURIComponent(clientId)}/diary/entries/${encodeURIComponent(id)}`)
      .then((raw) => normalizeDiaryEntry(raw?.item ?? raw));
  },

  listClientPrivateNotes(clientUserId, { q = "", page = 0, size = 50 } = {}) {
    const id = String(clientUserId || "").trim();
    if (!id) return Promise.reject(new Error("clientUserId is required"));
    const qs = new URLSearchParams();
    qs.set("page", String(Math.max(0, Number(page) || 0)));
    qs.set("size", String(Math.max(1, Number(size) || 50)));
    if (String(q || "").trim()) qs.set("q", String(q).trim());
    return request(`/api/psychologist/clients/${encodeURIComponent(id)}/notes?${qs.toString()}`)
      .then((raw) => extractItems(raw?.items ?? raw).map(normalizePsychologistClientNote));
  },

  createClientPrivateNote(clientUserId, payload = {}) {
    const id = String(clientUserId || "").trim();
    if (!id) return Promise.reject(new Error("clientUserId is required"));
    return request(`/api/psychologist/clients/${encodeURIComponent(id)}/notes`, {
      method: "POST",
      json: {
        shortLabel: payload.shortLabel || payload.title || null,
        note: payload.note || payload.content || "",
      },
    }).then((raw) => normalizePsychologistClientNote(raw?.item ?? raw));
  },

  updateClientPrivateNote(clientUserId, noteId, payload = {}) {
    const clientId = String(clientUserId || "").trim();
    const id = String(noteId || "").trim();
    if (!clientId || !id) return Promise.reject(new Error("clientUserId and noteId are required"));
    return request(`/api/psychologist/clients/${encodeURIComponent(clientId)}/notes/${encodeURIComponent(id)}`, {
      method: "PUT",
      json: {
        shortLabel: payload.shortLabel || payload.title || null,
        note: payload.note || payload.content || "",
      },
    }).then((raw) => normalizePsychologistClientNote(raw?.item ?? raw));
  },

  deleteClientPrivateNote(clientUserId, noteId) {
    const clientId = String(clientUserId || "").trim();
    const id = String(noteId || "").trim();
    if (!clientId || !id) return Promise.reject(new Error("clientUserId and noteId are required"));
    return request(`/api/psychologist/clients/${encodeURIComponent(clientId)}/notes/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  listNoteTemplates() {
    return request("/api/psychologist/diary/note-templates").then((raw) => extractItems(raw?.items ?? raw));
  },

  async getInviteLink() {
    const raw = await request("/api/me/psychologist/invite-link");
    return {
      raw,
      inviteLink: resolveInviteLink(raw),
    };
  },

  async createClientAuthLink(clientId, payload = {}) {
    const id = String(clientId || "").trim();
    if (!id) throw new Error("clientId is required");

    const raw = await requestFirstAvailable(
      [
        `/api/psychologist/clients/${encodeURIComponent(id)}/auth-link`,
        `/api/psychologist/clients/${encodeURIComponent(id)}/invite-link`,
      ],
      {
        method: "POST",
        json: {
          fullName: payload?.fullName ? String(payload.fullName).trim() : null,
          contact: payload?.contact ? String(payload.contact).trim() : null,
          forceCreateLinkedUser: payload?.forceCreateLinkedUser !== false,
        },
      }
    );

    const item = unwrapItem(raw);
    return {
      raw,
      inviteLink: resolveInviteLink(item),
      email: String(item?.email ?? item?.generatedEmail ?? item?.loginEmail ?? item?.login ?? "").trim(),
      expiresAt: item?.expiresAt ?? item?.expiredAt ?? item?.validUntil ?? null,
    };
  },
};
